import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { Frequency } from 'rrule';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup, useClosePopupOnNavigation } from '@gem/popups';
import {
  Button,
  ButtonTypes,
  PopupFooter,
  PopupHeader,
  RadioLabel,
  Selector,
  TextField,
} from '@gem/ui-kit';

import { DatePicker } from '../DatePicker/DatePicker';
import { FilterChip } from '../FilterChip/FilterChip';
import { EndEnum, FORM_ID } from './CustomRepeatPopup.constants';
import {
  ICustomRepeatPopupParams,
  useCustomRepeatPopupPresenter,
} from './CustomRepeatPopup.presenter';
import styles from './CustomRepeatPopup.styles.m.css';

export const CustomRepeatPopup: FC<ICommonPopupsProps<ICustomRepeatPopupParams>> = observer(
  (props) => {
    const { t } = useTranslation();
    useClosePopupOnNavigation(props.popupInstance);
    const { presenter, form } = useCustomRepeatPopupPresenter(props);
    const {
      control,
      handleSubmit,
      setValue,
      watch,
      formState: { isSubmitting, isValid, isValidating },
    } = form;
    const end = watch('end');
    const frequency = watch('frequency');
    const interval = form.watch('interval');

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'byweekday',
    });

    return (
      <Popup
        isOpened
        onClose={presenter.onClose}
      >
        <PopupHeader
          title={t('calendar_recurrence_custom_title')}
          onClose={presenter.onClose}
        />
        <form
          id={FORM_ID}
          className={styles.form}
          onSubmit={handleSubmit(presenter.onSubmit)}
        >
          <div className={styles.container}>
            <div className={styles.text}>{t('calendar_recurrence_repeat_every')}</div>
            <Controller
              control={control}
              name="interval"
              render={({ field }): ReactElement => (
                <TextField
                  {...field}
                  className={styles.intervalField}
                  type="number"
                  onKeyDown={presenter.handleKeyDown}
                  onPaste={presenter.handlePaste}
                />
              )}
            />
            <Controller
              control={control}
              name="frequency"
              render={({ field }): ReactElement => (
                <Selector
                  {...field}
                  items={presenter.getFrequencyItems(interval)}
                  onChange={(value: string): void => {
                    presenter.onChangeFrequency(value);
                    field.onChange(value);
                  }}
                  className={styles.typeField}
                  theme="standard"
                  placement="bottom-end"
                />
              )}
            />
          </div>
          {+frequency === Frequency.MONTHLY && (
            <div>
              <Controller
                control={control}
                name="isSameDay"
                render={({ field }): ReactElement => (
                  <RadioLabel
                    {...field}
                    value=""
                    isActive={field.value}
                    titleClassName={styles.radioLabelTitle}
                    title={`${t('calendar_recurrence_monthly_same_day')}`}
                    onChange={(): void => {
                      field.onChange(true);
                      setValue('isFirstMonday', false);
                      setValue('isLastDay', false);
                    }}
                  />
                )}
              />
              {presenter.isShowLastDayField && (
                <Controller
                  control={control}
                  name="isLastDay"
                  render={({ field }): ReactElement => (
                    <RadioLabel
                      {...field}
                      value=""
                      isActive={field.value}
                      titleClassName={styles.radioLabelTitle}
                      title={`${t('calendar_recurrence_monthly_last_day')}`}
                      onChange={(): void => {
                        field.onChange(true);
                        setValue('isSameDay', false);
                        setValue('isFirstMonday', false);
                      }}
                    />
                  )}
                />
              )}
              <Controller
                control={control}
                name="isFirstMonday"
                render={({ field }): ReactElement => (
                  <RadioLabel
                    {...field}
                    value=""
                    isActive={field.value}
                    titleClassName={styles.radioLabelTitle}
                    title={`${t('calendar_recurrence_monthly_first_monday')}`}
                    onChange={(): void => {
                      field.onChange(true);
                      setValue('isSameDay', false);
                      setValue('isLastDay', false);
                    }}
                  />
                )}
              />
            </div>
          )}
          {+frequency === Frequency.WEEKLY && (
            <div>
              <div className={styles.text}>{t('calendar_recurrence_repeat_on')}</div>
              <div className={styles.filters}>
                {presenter.weekdays.map((day) => (
                  <FilterChip
                    value={day.value}
                    title={day.title}
                    onClick={(value): void => {
                      const weekdayIndex = fields.findIndex((field) => field.value === value);

                      if (
                        weekdayIndex >= 0 &&
                        fields.length === 1 &&
                        presenter.initialValues.byweekday[0]?.value === value
                      ) {
                        return;
                      }

                      if (
                        weekdayIndex >= 0 &&
                        fields.length === 1 &&
                        presenter.initialValues.byweekday[0]?.value !== value
                      ) {
                        append({ value: presenter.initialValues.byweekday[0].value });
                      }

                      if (weekdayIndex >= 0) {
                        remove(weekdayIndex);
                      } else {
                        append({ value: day.value });
                      }
                    }}
                    active={fields.some((field) => field.value === day.value)}
                  />
                ))}
              </div>
            </div>
          )}
          <Controller
            control={control}
            name="end"
            render={({ field }): ReactElement => (
              <Selector
                {...field}
                label={t('calendar_recurrence_ends')}
                items={presenter.endItems}
                theme="standard"
                isFullWidth
              />
            )}
          />
          {end === EndEnum.OnDate && (
            <Controller
              control={control}
              name="until"
              render={({ field }): ReactElement => (
                <DatePicker
                  {...field}
                  label={t('calendar_recurrence_end_date')}
                />
              )}
            />
          )}
          {end === EndEnum.NumberOccurrences && (
            <div className={styles.container}>
              <div className={styles.text}>{t('calendar_recurrence_number_of_occurrences')}</div>
              <Controller
                control={control}
                name="count"
                render={({ field }): ReactElement => (
                  <TextField
                    {...field}
                    className={styles.numberOccurrencesField}
                    type="number"
                    onKeyDown={presenter.handleKeyDown}
                    onPaste={presenter.handlePaste}
                  />
                )}
              />
            </div>
          )}
        </form>
        <PopupFooter>
          <Button
            type={ButtonTypes.Submit}
            form={FORM_ID}
            isLoading={isSubmitting}
            isDisabled={!isValid || isValidating}
          >
            {t('common_done')}
          </Button>
        </PopupFooter>
      </Popup>
    );
  }
);
